"use client";

import { useImport } from "@/context/importContext";
import { useSession } from "@/context/SessionContext";
import { candidateIdentityRows } from "@/utils/import/candidatesImport";
import { MAX_PREVIEW_ROWS } from "@/utils/import/importChunks";
import { createValidationService } from "@/utils/import/validations/validationService";
import {
  checkImportUsageAction,
  importMatchCountryStateAction,
  previewImportCandidatesAction,
  type ImportUsageStatusType,
  type ImportCandidatesWarningType,
  type importMatchCountryStateType,
} from "@/utils/serverActions/import/importActions";
import { useCallback } from "react";
import { useImportStatic } from "./importHooks";

/**
 * What the plan allows and what the company has used. A failure of the question does
 * not block the import: the database refuses by itself what does not fit.
 */
const checkImportUsage = async (
  companyId: number,
  kind: string,
  rows: number,
): Promise<ImportUsageStatusType | null> => {
  if (kind !== "import_candidates" && kind !== "import_contracts") return null;
  try {
    return await checkImportUsageAction({ p_company_id: companyId, p_kind: kind, p_rows: rows });
  } catch (error) {
    console.error("Error checking the import limit:", error);
    return null;
  }
};

/**
 * Takes the rows of the import already matched to the columns of the selected
 * profile (one cell per field, in the order of the profile) and moves the wizard
 * on to the validation of every row.
 */
export const useProcessImportRows = () => {
  const { importData, setImportData } = useImport();
  const { selectedProfile } = importData;
  const { session } = useSession();
  const { languageCode, companyId } = session;
  const staticData = useImportStatic(languageCode);
  const previewCandidateWarnings = usePreviewCandidateWarnings();

  return useCallback(
    async (profileRows: string[][]) => {
      if (!selectedProfile || profileRows.length === 0) return;

      // The preview of candidates sends every row in one request: a bigger file is split first.
      if (selectedProfile.id === "candidates" && profileRows.length > MAX_PREVIEW_ROWS) {
        setImportData((prev) => ({
          ...prev,
          fileErrors: [
            {
              type: "error",
              columns: [],
              message: `El archivo tiene ${profileRows.length.toLocaleString("es")} filas y el máximo por archivo es ${MAX_PREVIEW_ROWS.toLocaleString("es")}. Divídelo en archivos más pequeños e impórtalos uno tras otro.`,
            },
          ],
          source: { ...prev.source, isProcessing: false },
        }));
        return;
      }

      setImportData((prev) => ({
        ...prev,
        fileErrors: [],
        source: { ...prev.source, isProcessing: true },
      }));

      try {
        // The countries (and their states) the rows name, matched by the database.
        // Without them every country was reported as invalid.
        let countryStateData: importMatchCountryStateType[] | null = null;
        const profileKeys = Object.keys(selectedProfile.columns);
        const countryIndex = profileKeys.indexOf("country");
        if (countryIndex >= 0) {
          const countryNames = Array.from(
            new Set(
              profileRows
                .map((cells) => cells[countryIndex]?.trim())
                .filter((name): name is string => Boolean(name)),
            ),
          );
          if (countryNames.length > 0) {
            countryStateData = await importMatchCountryStateAction({
              p_country_names: countryNames,
            });
          }
        }

        const validationService = createValidationService(
          selectedProfile.columns,
          languageCode,
          countryStateData,
          staticData,
          selectedProfile.atLeastOneOf,
        );

        const rows: Record<string, unknown>[] = [];
        const rowErrors: Array<{ row: number; field: string; error: string }> = [];

        profileRows.forEach((cells, index) => {
          const row: Record<string, unknown> = {};

          profileKeys.forEach((key, columnIndex) => {
            const column = selectedProfile.columns[key];
            const header = column.fieldHeader?.[languageCode] || column.fieldHeader?.en || key;
            const value = cells[columnIndex]?.trim() || "";
            // An e-mail is compared lower-cased everywhere: shown that way too.
            row[header] = column.type === "email" ? value.toLowerCase() : value;
          });
          rows.push(row);

          const validationResult = validationService.validateRow(row);
          if (!validationResult.isValid) {
            Object.entries(validationResult.errors).forEach(([field, error]) => {
              const column = selectedProfile.columns[field];
              rowErrors.push({
                row: index + 1,
                field: column?.fieldHeader?.[languageCode] || column?.fieldHeader?.en || field,
                error,
              });
            });
          }
        });

        // Candidates: what the database says about the people of the rows (an
        // e-mail of someone of the company with another name, a homonym...).
        const rowWarnings =
          selectedProfile.id === "candidates"
            ? await previewCandidateWarnings(rows)
            : [];

        // Do the rows fit what the plan has left? The import checks again by itself.
        const usage =
          selectedProfile.id === "candidates" || selectedProfile.id === "contracts"
            ? await checkImportUsage(companyId, `import_${selectedProfile.id}`, rows.length)
            : null;

        setImportData((prev) => ({
          ...prev,
          source: { ...prev.source, isProcessing: false },
          usage,
          totalRecords: rows.length - new Set(rowErrors.map((error) => error.row)).size,
          previewData: rows,
          rowErrors,
          rowWarnings,
          forcedRows: [],
          countryStateData,
          currentStep: "preview",
          isProcessing: false,
        }));
      } catch (error) {
        console.error("Error processing the import data:", error);
        setImportData((prev) => ({
          ...prev,
          source: { ...prev.source, isProcessing: false },
          fileErrors: [
            {
              message: `Error al procesar los datos: ${
                error instanceof Error ? error.message : "Error desconocido"
              }`,
              type: "error",
              columns: [],
            },
          ],
          isProcessing: false,
        }));
      }
    },
    [selectedProfile, companyId, languageCode, staticData, setImportData, previewCandidateWarnings],
  );
};

/**
 * Warnings of the database about the rows of a candidates import. They never block:
 * if the preview fails the import still applies the same rules.
 */
export const usePreviewCandidateWarnings = () => {
  const { importData } = useImport();
  const { selectedProfile } = importData;
  const { session } = useSession();
  const { languageCode, companyId } = session;

  return useCallback(
    async (rows: Record<string, unknown>[]): Promise<ImportCandidatesWarningType[]> => {
      if (rows.length === 0) return [];
      try {
        return await previewImportCandidatesAction({
          p_company_id: companyId,
          p_data: candidateIdentityRows(rows, selectedProfile, languageCode),
        });
      } catch (error) {
        console.error("Error previewing the candidates import:", error);
        return [];
      }
    },
    [selectedProfile, companyId, languageCode],
  );
};
